import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, ArrowRight, ShieldCheck, MailCheck, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from './api';
import { useAuth } from './AuthContext';
import { logger } from '../lib/logger';

/**
 * Public magic-link acceptance page.
 * Route: /app/invite/:token
 *
 * Renders a prefilled signup form using the token's stored client info,
 * then calls POST /api/coach-invites/{token}/accept to create/claim the
 * user account, apply the mapped tier grant, and issue a session cookie.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState('loading'); // loading | ready | done | error
  const [errMsg, setErrMsg] = useState('');
  const [prefill, setPrefill] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/api/coach-invites/${token}`);
        if (cancelled) return;
        setPrefill(data);
        setName(data.name || '');
        setEmail(data.email || '');
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        logger.error('Invite fetch failed', err);
        setErrMsg(String(err?.message || err) || 'This invite link is not valid.');
        setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/api/coach-invites/${token}/accept`, {
        password,
        name: name || undefined,
        email: email || undefined,
      });
      const tierLabel = res?.tier_applied?.label || 'account';
      toast.success('Welcome to Apex Athletic Mind', { description: `${tierLabel} unlocked.` });
      try { await refresh(); } catch { /* ignore */ }
      setState('done');
      // Small delay so the toast is visible, then push into the app.
      setTimeout(() => navigate('/app/dashboard', { replace: true }), 900);
    } catch (err) {
      logger.error('Invite accept failed', err);
      toast.error('Signup failed', { description: String(err?.message || err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="invite-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Invite unavailable</h1>
            <p className="text-sm text-muted-foreground">{errMsg}</p>
            <p className="text-xs text-muted-foreground">
              Ask Coach Lewis to send you a fresh invite, or sign in below if you already have an account.
            </p>
            <Button asChild className="w-full">
              <Link to="/app/login">Go to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <MailCheck className="h-10 w-10 text-emerald-600 mx-auto" />
          <h2 className="font-heading text-xl font-bold">You&apos;re in.</h2>
          <p className="text-sm text-muted-foreground">Taking you to the app…</p>
        </div>
      </div>
    );
  }

  const tierLabel = prefill?.tier_preview?.label || 'Free account';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4" data-testid="invite-accept-page">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 space-y-6">
          <div>
            <p className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-muted-foreground font-semibold">
              Personal invite from Coach Lewis
            </p>
            <h1 className="font-heading text-2xl font-bold mt-1">
              {prefill?.name ? `Welcome, ${prefill.name.split(' ')[0]}.` : 'Welcome.'}
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Finish signup to unlock the app. You&apos;ll be logged in as soon as you set a password.
            </p>
          </div>

          <div className="border rounded-lg p-3 bg-muted/30 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold">Access: {tierLabel}</div>
              <div className="text-xs text-muted-foreground">Applied automatically — no card required.</div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="invite-name">Your name</Label>
              <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name-input" />
            </div>
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={!!prefill?.email}
                className={prefill?.email ? 'bg-muted/50 cursor-not-allowed' : ''}
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="invite-password">Create a password</Label>
              <Input
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                placeholder="At least 8 characters"
                data-testid="invite-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="invite-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Claim your account
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center">
            By continuing you agree to Apex Athletic Mind&apos;s Terms and Privacy Policy.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
